import React, { useState } from 'react'
import useSWR from 'swr'
import { fetcher, apiRequest } from '../utils/api'
import { CreditCard, TrendingUp, Users, Clock, Crown, AlertTriangle, ArrowUp, ArrowDown, Plus, X } from 'lucide-react'
import PageHeader from '../components/admin/PageHeader'
import StatCard from '../components/admin/StatCard'
import DataTable from '../components/admin/DataTable'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog'

export default function Subscriptions() {
  const [page, setPage] = useState(1)
  const [plan, setPlan] = useState('')
  const [actionLoading, setActionLoading] = useState(null)
  const [showExtendModal, setShowExtendModal] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState(null)
  const [extendDays, setExtendDays] = useState(14)

  const { data, isLoading, mutate } = useSWR(`/api/admin/subscriptions?page=${page}&limit=20&plan=${plan}`, fetcher)
  const { data: summary } = useSWR('/api/admin/subscriptions/summary', fetcher)

  const handleUpgrade = async (teamId) => {
    if (!confirm('Upgrade this team to Premium?')) return
    setActionLoading(teamId)
    try { await apiRequest(`/api/admin/subscriptions/${teamId}/upgrade`, { method: 'POST' }); mutate() }
    catch (err) { alert(err.message) } finally { setActionLoading(null) }
  }

  const handleDowngrade = async (teamId) => {
    if (!confirm('Downgrade this team to Free? This will cancel any active subscription.')) return
    setActionLoading(teamId)
    try { await apiRequest(`/api/admin/subscriptions/${teamId}/downgrade`, { method: 'POST' }); mutate() }
    catch (err) { alert(err.message) } finally { setActionLoading(null) }
  }

  const handleExtendTrial = async () => {
    if (!selectedTeam) return
    setActionLoading(selectedTeam.team_id)
    try {
      await apiRequest(`/api/admin/subscriptions/${selectedTeam.team_id}/extend-trial`, { method: 'POST', body: JSON.stringify({ days: extendDays }) })
      mutate(); setShowExtendModal(false); setSelectedTeam(null)
    } catch (err) { alert(err.message) } finally { setActionLoading(null) }
  }

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'
  const formatCurrency = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)

  const isTrialExpiringSoon = (trialEndsAt) => {
    if (!trialEndsAt) return false
    const daysLeft = Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24))
    return daysLeft <= 7 && daysLeft > 0
  }

  const columns = [
    {
      key: 'name', header: 'Team', accessor: (row) => row.name,
      render: (row) => (<div><p className="font-medium text-gray-900">{row.name}</p><p className="text-xs text-gray-500">{row.sport}</p></div>),
    },
    {
      key: 'owner', header: 'Owner', accessor: (row) => row.owner_name,
      render: (row) => (<div><p className="font-medium text-gray-900">{row.owner_name}</p><p className="text-xs text-gray-500">{row.owner_email}</p></div>),
    },
    {
      key: 'plan', header: 'Plan', accessor: 'plan',
      render: (row) => {
        const p = row.plan
        return <Badge className={p === 'premium' ? 'bg-emerald-100 text-emerald-700' : p === 'trial' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}>
          {p === 'premium' && <Crown className="w-3 h-3 mr-1" />}{p === 'trial' && <Clock className="w-3 h-3 mr-1" />}{p || 'Free'}
        </Badge>
      },
    },
    {
      key: 'members', header: 'Members', accessor: 'member_count',
      render: (row) => <div className="flex items-center gap-2"><Users className="w-4 h-4 text-gray-400" /><span>{row.member_count}</span></div>,
    },
    {
      key: 'trial_ends', header: 'Trial Ends', accessor: 'trial_ends_at',
      render: (row) => row.trial_ends_at ? (
        <div className={isTrialExpiringSoon(row.trial_ends_at) ? 'text-orange-600' : ''}>
          {formatDate(row.trial_ends_at)}
          {isTrialExpiringSoon(row.trial_ends_at) && <p className="text-xs">Expiring soon!</p>}
        </div>
      ) : '-',
    },
    {
      key: 'actions', header: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.plan !== 'premium' && <Button variant="ghost" size="icon" className="text-emerald-600" onClick={() => handleUpgrade(row.team_id)} disabled={actionLoading === row.team_id} title="Upgrade"><ArrowUp className="w-4 h-4" /></Button>}
          {row.plan !== 'free' && <Button variant="ghost" size="icon" className="text-orange-500" onClick={() => handleDowngrade(row.team_id)} disabled={actionLoading === row.team_id} title="Downgrade"><ArrowDown className="w-4 h-4" /></Button>}
          {(row.plan === 'trial' || row.plan === 'free') && <Button variant="ghost" size="icon" className="text-indigo-600" onClick={() => { setSelectedTeam(row); setShowExtendModal(true) }} disabled={actionLoading === row.team_id} title="Extend Trial"><Plus className="w-4 h-4" /></Button>}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Subscription Management" description="Manage team subscriptions and trials" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Premium Teams" value={summary?.plan_counts?.premium || 0} icon={Crown} color="emerald" />
        <StatCard title="Active Trials" value={summary?.plan_counts?.trial || 0} icon={Clock} color="amber" />
        <StatCard title="Trials Expiring Soon" value={summary?.trials_expiring_soon || 0} icon={AlertTriangle} color="orange" />
        <StatCard title="Monthly Revenue" value={formatCurrency(summary?.mrr_cents || 0)} icon={TrendingUp} color="indigo" />
      </div>

      <div className="flex gap-4">
        <Select value={plan || 'all'} onValueChange={(val) => { setPlan(val === 'all' ? '' : val); setPage(1) }}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Plans" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div></div>
      ) : (
        <DataTable columns={columns} data={data?.subscriptions || []} sortable emptyMessage="No subscriptions found" />
      )}

      {data?.pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Page {page} of {data.pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages}>Next</Button>
          </div>
        </div>
      )}

      {/* Extend Trial Dialog */}
      <Dialog open={showExtendModal && !!selectedTeam} onOpenChange={(open) => { if (!open) { setShowExtendModal(false); setSelectedTeam(null) } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Extend Trial</DialogTitle></DialogHeader>
          <p className="text-gray-600">Extend trial for <strong>{selectedTeam?.name}</strong></p>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">Days to add</label>
            <Select value={String(extendDays)} onValueChange={(val) => setExtendDays(Number(val))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowExtendModal(false); setSelectedTeam(null) }}>Cancel</Button>
            <Button onClick={handleExtendTrial} disabled={actionLoading === selectedTeam?.team_id}>Extend Trial</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
